/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description ERAActions
 */
import { ButtonProps, Dropdown, Tooltip } from 'antd';
import { MoreOne } from '@icon-park/react';
import { MenuItemType } from 'antd/es/menu/interface';

import Button from '.';
import { defaultDropdownButtonProps } from './ClaimTableActions';
import useBlobDownload from 'hooks/mutate/useBlobDownload';
import useDownloadEra from 'hooks/mutate/useDownloadEra';
import { PAYMENT_RECONCILIATION_OUTCOME, PAYMENT_RECONCILIATION_STATUS } from 'app-constants/claims/era';
import { S3_BLOB_CATEGORY } from '@veritable/common/constants';

export interface ERAActionsProps extends ButtonProps {
	data: ERA.ERADetailsResponse | ERA.ERAListResponse;
	showAddNoteButton?: boolean;
}

const ERAActions: React.FC<ERAActionsProps> = ({ data, showAddNoteButton, ...restProps }) => {
	const canRevertERA =
		data?.fhirResource?.status === PAYMENT_RECONCILIATION_STATUS.active &&
		(data?.fhirResource?.outcome === PAYMENT_RECONCILIATION_OUTCOME.complete ||
			data?.fhirResource?.outcome === PAYMENT_RECONCILIATION_OUTCOME.partial);

	const { mutateAsync: downloadERA, isPending: isDownloadingERA } = useBlobDownload({
		title: data?.metadata?.eraNumber,
		contentType: 'text/plain',
	});

	const { mutateAsync: downloadInsuranceReport, isPending: isDownloadingReport } = useDownloadEra({ title: data?.metadata?.eraNumber });

	const attachmentMeta = data?.attachments?.find((attachment) => attachment?.fileType === 'application/EDI-X12');
	const exportERA = (e) => {
		e.stopPropagation();
		e.preventDefault();
		if (attachmentMeta?.blobId) {
			downloadERA({
				blobId: attachmentMeta?.blobId,
				blobCategory: S3_BLOB_CATEGORY.entityattachment,
				entityType: data?.fhirResource?.resourceType,
			});
		}
	};

	const downloadSchema: MenuItemType[] = [
		{
			key: 'export-era',
			className: 'tw-dropdown-menu-item',
			disabled: !attachmentMeta?.blobId,
			label: (
				<Button
					{...defaultDropdownButtonProps}
					onClick={(e) => exportERA(e)}
					disabled={isDownloadingERA}
					data-testid='ipr-action-download-x12'
				>
					<Tooltip title={attachmentMeta?.blobId ? '' : 'No Source Data File'}>{'Source Data File (835)'}</Tooltip>
				</Button>
			),
		},
		{
			key: 'download-insurance-report',
			className: 'tw-dropdown-menu-item',
			label: (
				<Button
					{...defaultDropdownButtonProps}
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
						downloadInsuranceReport({ paymentReconciliationId: data?.fhirResource?.id });
					}}
					disabled={isDownloadingReport}
					data-testid='ipr-action-download-pdf'
				>
					{'Insurance Payment Report (PDF)'}
				</Button>
			),
		},
	];

	const schema: MenuItemType[] = [
		{
			key: 'download',
			className: 'tw-dropdown-menu-item',
			onClick: (info) => info.domEvent.stopPropagation(),
			label: (
				<Dropdown menu={{ items: downloadSchema }} trigger={['hover']} align={{ points: ['tr', 'tl'], offset: [-4, 0] }}>
					<Button {...defaultDropdownButtonProps} data-testid='ipr-action-download'>
						{'Download'}
					</Button>
				</Dropdown>
			),
		},
		...(showAddNoteButton
			? [
					{
						key: 'add-note-button',
						className: 'tw-dropdown-menu-item',
						label: (
							<Button.EntityNote
								icon={null}
								entityType={data?.fhirResource?.resourceType}
								entityId={data?.fhirResource?.id}
								noteMeta={data?.userNote}
								{...defaultDropdownButtonProps}
								data-testid='ipr-action-add-note'
							/>
						),
					},
				]
			: []),
		...(canRevertERA
			? [
					{
						key: 'revert-reconcile-payment',
						className: 'tw-dropdown-menu-item',
						label: (
							<Button.RevertReconcilePayment data={data} {...defaultDropdownButtonProps} data-testid='ipr-action-revert' />
						),
						danger: true,
					},
				]
			: []),
	];

	return (
		<Dropdown menu={{ items: schema }} trigger={['click']}>
			<Button color='default' icon={<MoreOne />} onClick={(e) => e.stopPropagation()} data-testid='ipr-row-kebab' {...restProps} />
		</Dropdown>
	);
};

export default ERAActions;
